
import React, { useEffect, useState } from 'react';
import { 
  Check, Star, ArrowRight, Shield, Users, 
  Sparkles, Sparkle, Camera, BarChart3, PieChart, CreditCard, LayoutDashboard,
  Receipt, Crown, MousePointer2, Smartphone, Globe, Menu, Calendar, Zap, ShieldCheck, Loader2, X,
  Wallet, Target, Aperture, CheckCircle2, ScrollText, Handshake, Link, Bell, Layers
} from 'lucide-react';
import { Testimonial } from '../types';
import { createCheckoutSession } from '../services/stripeService';
import { auth } from '../services/firebase';
import { PricingModal } from './PricingModal';
import { BrandLogo } from './BrandLogo';
import { STRIPE_PLANS } from '../constants';

interface LandingProps {
  onAuth: (priceId?: string) => void;
}

const FIXED_TESTIMONIALS: Testimonial[] = [
  {
    name: "Carlos & Mariana",
    role: "Proprietários",
    content: "Conseguimos acompanhar cada detalhe da nossa casa juntos. O diário de fotos é incrível para registrar as conquistas e o controle financeiro é super simples!",
    avatar: "https://ui-avatars.com/api/?name=Carlos+Mariana&background=10B981&color=fff&size=128"
  },
  {
    name: "Eng. Pedro Santos",
    role: "Gestor de Obras",
    content: "O Minha Obra substituiu totalmente minhas planilhas de campo. O orçamento por etapas me ajuda a manter o controle do que já foi gasto em cada fase.",
    avatar: "https://ui-avatars.com/api/?name=Pedro+Santos&background=03624C&color=fff&size=128"
  },
  {
    name: "Arq. Sofia Rebouças",
    role: "Arquitetura e Interiores",
    content: "Indico para todos os meus clientes. O link para prestadores enviarem fotos facilita muito quando não posso estar na obra todo dia.",
    avatar: "https://ui-avatars.com/api/?name=Sofia+Reboucas&background=3B82F6&color=fff&size=128"
  }
];

export const Landing: React.FC<LandingProps> = ({ onAuth }) => {
  const [scrolled, setScrolled] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToFeatures = () => {
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePlanSelection = async (priceId: string) => {
    if (!auth.currentUser) {
      onAuth(priceId);
      return;
    }

    setLoadingCheckout(true);
    try {
      await createCheckoutSession(priceId);
    } catch (error) {
      alert("Erro ao iniciar pagamento. Tente novamente.");
      setLoadingCheckout(false);
    }
  };

  const faqItems = [
    { q: 'Preciso entender de Excel ou finanças?', a: 'Zero! O app foi desenhado para ser intuitivo. Você lança os gastos como se estivesse mandando uma mensagem e nós organizamos tudo.' },
    { q: 'Posso compartilhar com meu cônjuge?', a: 'Sim! Nos planos pagos, você tem acesso simultâneo para o casal acompanhar a obra junto, em tempo real e de celulares diferentes.' },
    { q: 'Meus dados estão seguros?', a: 'Utilizamos a infraestrutura do Google Cloud. Suas fotos e dados financeiros estão criptografados e salvos na nuvem com backup automático.' },
    { q: 'Serve para pequenas reformas?', a: 'Perfeito para qualquer tamanho. De um banheiro novo à construção de um prédio, a organização financeira é essencial para não estourar o orçamento.' },
    { q: 'O que é o Orçamento Detalhado?', a: 'É uma função do plano PRO+ onde você define limites de gasto específicos para cada etapa da obra (Ex: R$ 50k para Fundação), permitindo um controle muito mais preciso.' },
    { q: 'Consigo enviar fotos para o banco?', a: 'Sim, nosso gerador de relatórios cria um PDF organizado com as fotos e datas, ideal para vistorias de medição bancária.' }
  ];

  // Funcionalidades Completas com Descrições Detalhadas (Igual FeaturesPage)
  const features = [
    {
      title: "Gestão Financeira",
      subtitle: "Cada centavo conta",
      description: "Centralize todas as despesas da obra em um único lugar. Esqueça as notas fiscais perdidas e as planilhas confusas.",
      icon: Wallet,
      color: "bg-emerald-100 text-emerald-700",
      items: ["Categorização automática", "Gestão de parcelamentos", "Controle de pagamentos"]
    },
    {
      title: "Controle Orçamentário",
      subtitle: "Metas e Limites",
      description: "Defina o teto de gastos da sua obra e acompanhe o realizado versus orçado em tempo real.",
      icon: Target,
      color: "bg-emerald-200 text-emerald-800",
      items: ["Saldo em tempo real", "Gráficos de consumo", "Alertas de estouro"]
    },
    {
      title: "Diário de Obra Visual",
      subtitle: "Histórico Completo",
      description: "Mais do que fotos, um histórico organizado. Registre a evolução diária da obra e tenha uma linha do tempo visual.",
      icon: Aperture,
      color: "bg-green-100 text-green-700",
      items: ["Linha do tempo cronológica", "Organização por ambientes", "Backup na nuvem"]
    },
    {
      title: "Relatórios e Análises",
      subtitle: "Dados para decisão",
      description: "Transforme dados em inteligência. Gere relatórios PDF profissionais para enviar ao banco ou sócios.",
      icon: ScrollText,
      color: "bg-teal-100 text-teal-800",
      items: ["Exportação de PDF", "Fluxo de caixa futuro", "Resumo executivo"]
    },
    {
      title: "Colaboração",
      subtitle: "Multi-Usuário",
      description: "Convide seu cônjuge, engenheiro ou arquiteto. Todos visualizam as mesmas informações em tempo real.",
      icon: Handshake,
      color: "bg-cyan-100 text-cyan-800",
      items: ["Convites via link", "Sincronização real-time", "Gestão de permissões"]
    },
    {
      title: "Link do Prestador",
      subtitle: "Acesso Externo",
      description: "Envie um link único para pedreiros e fornecedores enviarem fotos sem precisar baixar o app.",
      icon: Link,
      color: "bg-sky-100 text-sky-800",
      items: ["Upload sem login", "Link compartilhável", "Recebimento de fotos"]
    },
    {
      title: "Orçamento por Etapas",
      subtitle: "Planejamento Faseado",
      description: "Divida sua obra em fases (Fundação, Acabamento) e defina orçamentos específicos para cada uma.",
      icon: Layers,
      color: "bg-blue-100 text-blue-800",
      items: ["Metas por etapa", "Comparativo Real x Orçado", "Gestão micro"]
    },
    {
      title: "Notificações",
      subtitle: "Alertas Inteligentes",
      description: "Receba alertas automáticos sobre limites de orçamento e lembretes para registrar fotos.",
      icon: Bell,
      color: "bg-amber-100 text-amber-700",
      items: ["Alertas de orçamento", "Lembretes de fotos", "Avisos de vencimento"]
    }
  ];

  return (
    <div className="mesh-gradient min-h-screen text-slate-900 dark:text-slate-100 selection:bg-brand-100 selection:text-brand-900 overflow-x-hidden transition-colors duration-300">
      
      {/* Floating Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex justify-between items-center ${scrolled ? 'sm:top-4' : 'top-0'}`}>
        <div className={`glass border border-slate-200/50 dark:border-slate-800/50 w-full px-4 sm:px-6 py-3 flex justify-between items-center rounded-2xl sm:rounded-full shadow-2xl transition-all ${scrolled ? 'py-2 px-4 scale-95' : ''}`}>
          <BrandLogo size="md" />
          
          <div className="flex items-center gap-4 sm:gap-6">
            <button 
              onClick={handleScrollToFeatures}
              className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-emerald-600 transition-colors"
            >
              Funcionalidades
            </button>
            
            <button 
              onClick={() => onAuth()}
              className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.1em] sm:tracking-[0.2em] text-white px-4 sm:px-6 py-2.5 sm:py-3 bg-brand-600 rounded-full hover:bg-brand-700 hover:shadow-brand-500/40 transition-all active:scale-95 shadow-xl shadow-brand-500/20"
            >
              Entrar
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-4 sm:px-6 pt-28 sm:pt-36 pb-0 text-center relative overflow-hidden flex flex-col items-center">
        <div className="absolute top-40 left-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-[100px] animate-pulse pointer-events-none"></div>
        <div className="absolute bottom-40 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-[120px] animate-pulse delay-700 pointer-events-none"></div>
        
        <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md border border-brand-100 dark:border-slate-800 shadow-lg rounded-full text-[8px] sm:text-[9px] font-black text-brand-600 uppercase tracking-widest relative z-10">
          <Sparkles size={12} className="text-brand-500 animate-spin-slow" /> GESTÃO INTELIGENTE
        </div>
        
        <h1 className="text-[40px] sm:text-6xl md:text-[80px] font-black text-slate-900 dark:text-white mb-6 leading-[1.05] sm:leading-[0.9] tracking-tighter relative z-10 max-w-5xl mx-auto uppercase text-balance">
          Controle <br className="hidden sm:block"/> da sua obra, <span className="text-brand-600 inline-block transform hover:rotate-1 transition-transform cursor-default">de forma simples.</span>
        </h1>
        
        <p className="text-sm sm:text-xl md:text-2xl text-slate-500 dark:text-slate-400 mb-10 max-w-3xl mx-auto leading-relaxed font-bold relative z-10 px-4 tracking-tight">
          Registre tudo em menos de um minuto
        </p>

        <div className="w-full max-w-[280px] sm:max-w-md relative z-10 flex flex-col gap-4">
          <button 
            onClick={() => onAuth()}
            className="group relative w-full bg-brand-600 text-white py-5 sm:py-6 px-8 rounded-2xl sm:rounded-[2.5rem] font-black text-xs sm:text-sm uppercase tracking-widest shadow-2xl shadow-brand-500/30 flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            <span className="relative z-10">Começar Agora</span>
            <ArrowRight size={20} className="relative z-10 group-hover:translate-x-2 transition-transform" />
          </button>
          
          <p className="text-[7px] sm:text-[10px] font-black uppercase text-slate-400 tracking-widest">Teste grátis • Sem cartão de crédito</p>
        </div>

        {/* Hero Image Mockup Preview */}
        <div className="mt-12 sm:mt-16 relative w-full max-w-6xl mx-auto px-4 pb-20 sm:pb-32">
          <div className="absolute inset-0 bg-brand-500/20 blur-[120px] -z-10 rounded-full"></div>
          
          {/* Desktop Mockup (CSS only) */}
          <div className="bg-slate-900 rounded-2xl sm:rounded-[2.5rem] border-4 sm:border-[12px] border-slate-800 shadow-2xl overflow-hidden aspect-[16/10] sm:aspect-video group flex flex-col">
            {/* Browser Header */}
            <div className="bg-slate-800/80 px-4 py-2 sm:py-4 flex items-center justify-between border-b border-slate-700/50">
              <div className="flex gap-1.5">
                <div className="size-2 sm:size-3 rounded-full bg-red-400/30"></div>
                <div className="size-2 sm:size-3 rounded-full bg-amber-400/30"></div>
                <div className="size-2 sm:size-3 rounded-full bg-emerald-400/30"></div>
              </div>
              <div className="bg-slate-900/50 px-3 py-1 rounded-md sm:rounded-lg border border-slate-700/30 flex items-center gap-2">
                <Globe size={10} className="text-slate-500 hidden sm:block" />
                <span className="text-[6px] sm:text-[8px] text-slate-400 font-black tracking-widest uppercase">www.minhaobra.shop</span>
              </div>
              <div className="size-5 sm:size-8 rounded-md bg-emerald-800/40 border border-emerald-500/20 flex items-center justify-center text-[6px] sm:text-[8px] text-white font-black">JS</div>
            </div>

            {/* Desktop App UI Content */}
            <div className="flex-1 flex bg-slate-50 dark:bg-slate-950 overflow-hidden relative">
               <div className="w-16 sm:w-24 border-r border-slate-200 dark:border-slate-800 flex flex-col items-center py-6 gap-6 hidden sm:flex">
                  <div className="size-10 bg-brand-600 rounded-xl"></div>
                  <div className="space-y-4 w-full px-4">
                     {[1,2,3,4].map(i => <div key={i} className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded-full"></div>)}
                  </div>
               </div>
               <div className="flex-1 flex flex-col p-4 sm:p-8 space-y-4 sm:space-y-8">
                  <div className="flex justify-between items-center">
                    <div className="h-4 sm:h-8 w-1/3 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
                    <div className="flex gap-2">
                       <div className="size-8 bg-slate-200 dark:bg-slate-800 rounded-full"></div>
                       <div className="size-8 bg-brand-600 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                     <div className="h-24 sm:h-48 bg-emerald-900/10 rounded-2xl sm:rounded-[2rem] border border-emerald-500/10 relative overflow-hidden p-6 flex flex-col justify-end">
                        <div className="w-1/2 h-4 bg-emerald-600/20 rounded-full mb-2"></div>
                        <div className="w-3/4 h-8 bg-emerald-600 rounded-lg"></div>
                     </div>
                     <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="h-10 sm:h-20 bg-slate-100 dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800"></div>
                        <div className="h-10 sm:h-20 bg-slate-100 dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800"></div>
                        <div className="h-10 sm:h-20 bg-slate-100 dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800"></div>
                        <div className="h-10 sm:h-20 bg-slate-100 dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800"></div>
                     </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                     {[1,2,3].map(i => (
                        <div key={i} className="h-10 sm:h-16 w-full bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl sm:rounded-2xl flex items-center px-4 gap-4">
                           <div className="size-6 sm:size-10 bg-slate-100 dark:bg-slate-800 rounded-lg"></div>
                           <div className="h-2 sm:h-3 w-1/3 bg-slate-100 dark:bg-slate-800 rounded-full"></div>
                           <div className="ml-auto h-2 sm:h-3 w-16 bg-brand-100 dark:bg-brand-900 rounded-full"></div>
                        </div>
                     ))}
                  </div>
               </div>
            </div>
          </div>

          {/* Mobile Overlay Mockup */}
          <div className="absolute -bottom-8 sm:-bottom-12 right-[5%] sm:right-[10%] w-[150px] sm:w-[280px] md:w-[320px] aspect-[9/19] bg-slate-950 rounded-[2.5rem] sm:rounded-[3.5rem] border-4 sm:border-[10px] border-slate-900 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] z-20 flex flex-col p-2 sm:p-4 animate-float overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 sm:w-32 h-3 sm:h-6 bg-slate-900 rounded-b-xl sm:rounded-b-3xl z-30 flex items-center justify-center">
               <div className="w-12 h-1 bg-slate-800 rounded-full"></div>
            </div>
            
            <div className="flex-1 bg-white dark:bg-slate-950 rounded-2xl sm:rounded-[2.5rem] overflow-hidden flex flex-col">
              <div className="px-3 pt-6 sm:pt-8 pb-3 flex justify-between items-center">
                <div className="size-6 sm:size-10 bg-slate-50 dark:bg-slate-900 rounded-lg flex items-center justify-center text-slate-400"><Menu size={14}/></div>
                <div className="text-[7px] sm:text-[11px] font-black uppercase tracking-tighter">Minha Casa</div>
                <div className="size-6 sm:size-10 bg-emerald-800 rounded-lg flex items-center justify-center text-[7px] sm:text-[10px] text-white font-black">JS</div>
              </div>

              <div className="overflow-y-auto flex-1 no-scrollbar space-y-4 px-3">
                  <div className="bg-[#064e3b] p-3 sm:p-6 rounded-xl sm:rounded-[2rem] shadow-xl flex flex-col gap-3 text-left relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10"><Sparkle size={40} /></div>
                    <div className="space-y-1 relative z-10">
                      <div className="text-[5px] sm:text-[8px] font-bold text-white/50 uppercase tracking-widest">Total Gasto</div>
                      <div className="text-xs sm:text-2xl font-black text-white tabular-nums tracking-tighter">R$ 142.400</div>
                    </div>
                    <div className="h-1 w-full bg-emerald-950/50 rounded-full overflow-hidden">
                      <div className="h-full w-2/3 bg-emerald-400"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                     {[
                       { icon: CreditCard, label: 'Novo Gasto', col: 'bg-emerald-50 text-emerald-600' },
                       { icon: Camera, label: 'Tirar Foto', col: 'bg-indigo-50 text-indigo-600' }
                     ].map((btn, i) => (
                       <div key={i} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-2 sm:p-4 rounded-xl flex items-center gap-2 shadow-sm">
                          <div className={`size-5 sm:size-10 ${btn.col} rounded-md flex items-center justify-center`}><btn.icon size={12}/></div>
                          <span className="text-[5px] sm:text-[8px] font-black uppercase tracking-widest text-slate-500 leading-none">{btn.label}</span>
                       </div>
                     ))}
                  </div>

                  <div className="space-y-2">
                     <p className="text-[7px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Recentes</p>
                     {[1,2,3].map(i => (
                       <div key={i} className="flex justify-between items-center p-2 sm:p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                          <div className="flex gap-2 items-center">
                             <div className="size-6 sm:size-8 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-300"><Receipt size={12}/></div>
                             <div className="w-12 h-2 bg-slate-200 dark:bg-slate-800 rounded-full"></div>
                          </div>
                          <div className="w-8 h-2 bg-emerald-100 dark:bg-emerald-900 rounded-full"></div>
                       </div>
                     ))}
                  </div>
              </div>

              <div className="h-10 sm:h-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-100 dark:border-slate-800 flex justify-around items-center px-2 sm:px-4 mt-auto">
                 {[LayoutDashboard, BarChart3, Camera, Users].map((Icon, i) => (
                   <div key={i} className={`flex flex-col items-center gap-1 ${i===0 ? 'text-emerald-600 scale-110' : 'text-slate-300'}`}>
                      <Icon size={12} strokeWidth={2.5}/>
                   </div>
                 ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 sm:py-20 bg-white dark:bg-slate-900 relative">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row gap-12 sm:gap-16 items-center">
           <div className="md:w-1/2 space-y-8 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 dark:bg-brand-900/30 rounded-lg text-[9px] sm:text-[10px] font-black text-brand-600 uppercase tracking-widest border border-brand-100 dark:border-brand-900/50">
                <Shield size={12} /> Plataforma Completa
              </div>
              
              <div className="space-y-4">
                <h3 className="text-4xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tighter uppercase leading-none">
                   Onde obras <br/>
                   <span className="text-brand-600">bem-feitas começam.</span>
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-bold text-sm sm:text-base leading-relaxed max-w-lg mx-auto md:mx-0">
                   Mais de 100 obras cadastradas já escolheram um jeito mais inteligente de construir.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 pt-4 border-t border-slate-100 dark:border-slate-800">
                 <div>
                    <p className="text-3xl sm:text-4xl font-black text-brand-600">+100</p>
                    <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">OBRAS CADASTRADAS</p>
                 </div>
                 <div>
                    <p className="text-3xl sm:text-4xl font-black text-brand-600">96%</p>
                    <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">DOS USUÁRIOS RECOMENDAM</p>
                 </div>
              </div>
           </div>
           
           <div className="md:w-1/2 w-full relative h-[400px] sm:h-[500px]">
               {/* 3D Mockup Container - Moved here from Features section */}
               <div className="relative w-full h-full perspective-1000 select-none scale-90 sm:scale-100 origin-center">
                  
                  {/* Card 1: Reports (Back Layer) */}
                  <div className="absolute top-0 right-[15%] sm:right-[20%] w-48 sm:w-64 bg-white dark:bg-slate-900 rounded-3xl p-4 shadow-lg border border-slate-100 dark:border-slate-800 transform rotate-6 opacity-80 scale-90 z-0">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest">Desempenho</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white">Relatórios</p>
                      </div>
                      <div className="p-2 bg-emerald-900 rounded-lg text-white"><ArrowRight size={12}/></div>
                    </div>
                    <div className="flex justify-between items-end mb-2">
                       <p className="text-[8px] font-bold text-slate-400">CUSTO / M²</p>
                       <p className="text-xs font-black text-slate-900 dark:text-white">R$ 1.250</p>
                    </div>
                    <div className="space-y-2">
                       <div className="bg-slate-100 dark:bg-slate-800 h-2 w-full rounded-full"></div>
                       <div className="bg-slate-100 dark:bg-slate-800 h-2 w-2/3 rounded-full"></div>
                    </div>
                  </div>

                  {/* Card 2: Right White Card (New Launch) */}
                  <div className="absolute top-20 right-0 hidden sm:block w-72 bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-xl border border-slate-100 dark:border-slate-800 z-10 transform translate-x-10 translate-y-10">
                     <div className="flex justify-between mb-6">
                        <div className="flex items-center gap-2"><div className="size-2 bg-emerald-500 rounded-full"></div> <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Novo Gasto</span></div>
                        <X size={14} className="text-slate-300"/>
                     </div>
                     <div className="text-center mb-6">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Valor Total</p>
                        <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">R$ 0,00</p>
                     </div>
                     <div className="flex bg-slate-50 dark:bg-slate-800 p-1 rounded-xl mb-4">
                        {['Materiais', 'Mão de Obra', 'Docs'].map((t, i) => (
                           <div key={t} className={`flex-1 py-2 text-[8px] font-black uppercase text-center rounded-lg ${i===0 ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-400'}`}>{t}</div>
                        ))}
                     </div>
                     <div className="h-10 w-full bg-slate-50 dark:bg-slate-800 rounded-xl mb-2"></div>
                     <div className="flex justify-between items-center mt-4">
                        <p className="text-[10px] font-bold text-slate-900 dark:text-white">12 Jan 2024</p>
                        <div className="bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full text-[8px] font-black uppercase text-slate-500">Fundação</div>
                     </div>
                  </div>

                  {/* Card 3: Left Photo Card */}
                  <div className="absolute bottom-0 left-0 sm:left-10 w-56 sm:w-64 bg-white dark:bg-slate-900 rounded-[2rem] p-3 shadow-2xl border border-slate-100 dark:border-slate-800 z-20 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                     <div className="relative aspect-[4/3] bg-slate-200 dark:bg-slate-800 rounded-[1.5rem] mb-3 overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=400&q=80" className="w-full h-full object-cover" alt="Construction"/>
                        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md px-2 py-1 rounded-lg text-[8px] font-black text-white uppercase tracking-widest">03 Jan</div>
                        <div className="absolute bottom-3 left-3 text-white">
                           <p className="text-[8px] font-bold uppercase tracking-widest opacity-80">Status Geral</p>
                           <p className="text-xs font-black leading-none">Fundação Concluída</p>
                        </div>
                     </div>
                     <div className="grid grid-cols-2 gap-2">
                        <div className="bg-blue-500 text-white p-3 rounded-2xl text-center">
                           <Camera size={16} className="mx-auto mb-1"/>
                           <p className="text-[8px] font-black uppercase leading-tight">Fotos</p>
                           <p className="text-[10px] font-black">Diário</p>
                        </div>
                        <div className="bg-emerald-500 text-white p-3 rounded-2xl text-center">
                           <CreditCard size={16} className="mx-auto mb-1"/>
                           <p className="text-[8px] font-black uppercase leading-tight">Registrar</p>
                           <p className="text-[10px] font-black">Despesa</p>
                        </div>
                     </div>
                  </div>

                  {/* Card 4: Main Center Green Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 bg-[#064E3B] text-white rounded-[2.5rem] p-8 shadow-2xl z-30 transform hover:scale-105 transition-all duration-500 border border-emerald-800">
                     <div className="flex justify-between items-start mb-6">
                        <div>
                           <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Total Realizado</p>
                           <p className="text-4xl font-black tracking-tighter">R$ 28.847</p>
                        </div>
                        <div className="text-right">
                           <p className="text-3xl font-black text-emerald-400 leading-none">9%</p>
                           <p className="text-[8px] font-bold text-emerald-600 uppercase tracking-widest">Executado</p>
                        </div>
                     </div>
                     
                     <div className="flex justify-between text-[10px] font-bold text-emerald-400/60 uppercase tracking-widest mb-1">
                        <span>Meta</span>
                        <span>Previsão</span>
                     </div>
                     <div className="flex justify-between text-sm font-bold mb-6">
                        <span>R$ 314.000</span>
                        <span>R$ 99.283</span>
                     </div>

                     <div className="space-y-1 mb-8">
                        <div className="flex justify-between text-[8px] font-black uppercase tracking-widest text-emerald-500">
                           <span>Progresso</span>
                           <span>9%</span>
                        </div>
                        <div className="h-2 bg-emerald-900 rounded-full overflow-hidden">
                           <div className="h-full w-[9%] bg-emerald-400 rounded-full"></div>
                        </div>
                     </div>

                     <div className="grid grid-cols-2 gap-3">
                        <div className="bg-emerald-900/50 p-3 rounded-2xl border border-emerald-800/50">
                           <p className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Economia</p>
                           <div className="flex items-center gap-1">
                              <ArrowRight className="rotate-45 text-emerald-400" size={12}/>
                              <p className="text-sm font-black">0.0%</p>
                           </div>
                        </div>
                        <div className="bg-white/10 p-3 rounded-2xl flex items-center justify-center border border-white/10 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-white/20 transition-colors text-center">
                           Editar Orçamento
                        </div>
                     </div>
                  </div>
               </div>
           </div>
        </div>
      </section>

      {/* Feature Grid - EXPANDABLE CARDS */}
      <section id="features" className="py-12 sm:py-24 bg-slate-50 dark:bg-slate-950 overflow-hidden px-4">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
             <h2 className="text-[9px] sm:text-[11px] font-black text-brand-600 uppercase tracking-[0.4em] mb-4">FUNCIONALIDADES</h2>
             <h3 className="text-3xl sm:text-6xl font-black text-slate-900 dark:text-white tracking-tighter uppercase leading-none mb-6">
               TUDO O QUE VOCÊ <br/>PRECISA
             </h3>
             <p className="text-slate-500 dark:text-slate-400 text-sm sm:text-lg leading-relaxed font-bold">
               Passe o mouse sobre os cards para descobrir o poder de cada ferramenta.
             </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div 
                key={i} 
                className="group relative bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-100 dark:border-slate-800 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 ${f.color.split(' ')[0].replace('text', 'bg')}`}></div>

                <div className="relative z-10 flex flex-col h-full">
                   <div className={`w-14 h-14 ${f.color} rounded-2xl flex items-center justify-center mb-6 shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                      <f.icon size={28} />
                   </div>
                   
                   <h4 className="font-black text-xl mb-1 text-slate-900 dark:text-white uppercase tracking-tight leading-none group-hover:text-emerald-700 transition-colors">{f.title}</h4>
                   <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2 opacity-80">{f.subtitle}</p>
                   
                   {/* Expandable Content */}
                   <div className="max-h-0 opacity-0 group-hover:max-h-[400px] group-hover:opacity-100 transition-all duration-700 ease-in-out overflow-hidden">
                      <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed font-bold mb-4 pt-2">
                        {f.description}
                      </p>
                      <ul className="space-y-2">
                        {f.items.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                             <div className="size-1.5 rounded-full bg-emerald-500"></div>
                             {item}
                          </li>
                        ))}
                      </ul>
                   </div>
                   
                   {/* Initial compact hint */}
                   <div className="group-hover:opacity-0 transition-opacity duration-300 absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-slate-900 to-transparent"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 mb-12 sm:mb-20 text-center md:text-left">
             <div>
                <h2 className="text-[9px] sm:text-[11px] font-black text-brand-600 uppercase tracking-[0.4em] mb-4">Depoimentos</h2>
                <h3 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tighter uppercase leading-none">Quem usa, aprova</h3>
             </div>
             <div className="flex gap-2">
                <div className="size-10 sm:size-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400"><MousePointer2 size={18}/></div>
                <div className="size-10 sm:size-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400"><Smartphone size={18}/></div>
             </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
            {FIXED_TESTIMONIALS.map((t, i) => (
              <div key={i} className="group bg-slate-50/50 dark:bg-slate-950/50 p-8 rounded-3xl sm:rounded-[3rem] border border-slate-100 dark:border-slate-800 hover:bg-white dark:hover:bg-slate-900 hover:shadow-2xl transition-all">
                 <div className="flex items-center gap-4 mb-6 sm:mb-10">
                    <img src={t.avatar} className="size-12 sm:size-14 rounded-xl sm:rounded-2xl object-cover shadow-lg border-2 border-white dark:border-slate-800 group-hover:scale-105 transition-all" alt={t.name} />
                    <div>
                       <p className="font-black text-slate-900 dark:text-white uppercase tracking-tight text-xs sm:text-sm">{t.name}</p>
                       <p className="text-[8px] sm:text-[9px] font-black text-brand-600 uppercase tracking-widest">{t.role}</p>
                    </div>
                 </div>
                 <p className="text-slate-600 dark:text-slate-400 font-bold italic leading-relaxed text-xs sm:text-sm mb-6 sm:mb-8">"{t.content}"</p>
                 <div className="flex gap-1">
                    {[1,2,3,4,5].map(s => <Star key={s} size={10} className="fill-brand-500 text-brand-500" />)}
                 </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - EXIBIÇÃO DIRETA */}
      <section id="plans" className="py-12 sm:py-24 bg-slate-50 dark:bg-slate-950 px-4">
        <div className="max-w-6xl mx-auto text-center">
           <div className="mb-10 space-y-4">
             <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 dark:bg-brand-900/30 rounded-full text-[9px] font-black text-brand-600 uppercase tracking-widest border border-brand-100 dark:border-brand-900/50">
               <Crown size={12} /> Planos e Preços
             </div>
             <h3 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tighter uppercase leading-none">Escolha <br/><span className="text-brand-600">o seu plano</span></h3>
             <p className="text-slate-500 dark:text-slate-400 font-bold text-sm max-w-lg mx-auto">Comece gratuitamente e evolua conforme sua necessidade.</p>
           </div>
           
           {/* Toggle Mensal/Anual */}
           <div className="flex justify-center mb-12">
            <div className="bg-white dark:bg-slate-900 p-1.5 rounded-full flex relative border border-slate-200 dark:border-slate-800 shadow-sm">
              {/* Pill Background Animation */}
              <div 
                className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-brand-600 rounded-full transition-all duration-300 shadow-md ${cycle === 'monthly' ? 'left-1.5' : 'left-[calc(50%+1.5px)]'}`}
              ></div>
              
              <button 
                onClick={() => setCycle('monthly')} 
                className={`relative z-10 px-8 sm:px-10 py-3 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest transition-colors ${cycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}
              >
                Mensal
              </button>
              <button 
                onClick={() => setCycle('yearly')} 
                className={`relative z-10 px-8 sm:px-10 py-3 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest transition-colors ${cycle === 'yearly' ? 'text-white' : 'text-slate-500'}`}
              >
                Anual
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-950 border border-amber-300 text-[8px] sm:text-[10px] px-2 py-0.5 rounded-full font-black whitespace-nowrap shadow-sm">
                  20% OFF
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
             {/* Plano Premium */}
             <div className="group relative rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col overflow-hidden text-left hover:scale-[1.02] transition-all">
                 <div className="p-8 pb-0">
                    <div className="flex justify-between items-start mb-4">
                       <div>
                          <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{STRIPE_PLANS.premium.label}</h3>
                          <p className="text-xs text-slate-500 font-bold mt-1">{STRIPE_PLANS.premium.description}</p>
                       </div>
                       <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-emerald-600 border border-emerald-100 dark:border-emerald-800/50">
                          <ShieldCheck size={20} />
                       </div>
                    </div>
                    
                    <div className="flex items-baseline gap-1 mt-6">
                       <span className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">R$ {STRIPE_PLANS.premium.price[cycle].toFixed(2).replace('.', ',')}</span>
                       <span className="text-xs text-slate-400 font-black uppercase tracking-wide">/{cycle === 'monthly' ? 'mês' : 'ano'}</span>
                    </div>
                 </div>
                 
                 <div className="p-8 flex-1 flex flex-col">
                    <div className="h-px bg-slate-100 dark:bg-slate-800 w-full mb-6"></div>
                    <ul className="space-y-4 mb-8 flex-1">
                       {STRIPE_PLANS.premium.features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300 font-bold">
                             <div className="mt-0.5 p-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600">
                               <Check size={10} strokeWidth={4}/>
                             </div>
                             <span>{feat}</span>
                          </li>
                       ))}
                    </ul>
                    <button 
                       onClick={() => handlePlanSelection(STRIPE_PLANS.premium[cycle])}
                       disabled={loadingCheckout}
                       className="w-full py-4 rounded-xl border-2 border-brand-600 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/10 font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2"
                    >
                       {loadingCheckout ? <Loader2 size={16} className="animate-spin"/> : 'Selecionar Plano'}
                    </button>
                 </div>
              </div>

              {/* Plano PRO+ */}
              <div className="group relative rounded-[2.5rem] bg-slate-900 border-2 border-brand-500 shadow-2xl flex flex-col overflow-hidden text-left hover:scale-[1.02] transition-all animate-card-breathing">
                 <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-500 text-slate-950 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-brand-500/20 z-20">
                    <Sparkles size={12} fill="currentColor" /> Recomendado
                 </div>

                 <div className="p-8 pb-0">
                    <div className="flex justify-between items-start mb-4">
                       <div>
                          <h3 className="text-xl font-black text-white uppercase tracking-tight">{STRIPE_PLANS.pro.label}</h3>
                          <p className="text-xs text-brand-100/60 font-bold mt-1">{STRIPE_PLANS.pro.description}</p>
                       </div>
                       <div className="p-3 bg-brand-500 rounded-xl text-slate-950 shadow-lg shadow-brand-500/20">
                          <Crown size={20} fill="currentColor" />
                       </div>
                    </div>
                    
                    <div className="flex items-baseline gap-1 mt-6">
                       <span className="text-4xl font-black text-white tracking-tight">R$ {STRIPE_PLANS.pro.price[cycle].toFixed(2).replace('.', ',')}</span>
                       <span className="text-xs text-brand-100/40 font-black uppercase tracking-wide">/{cycle === 'monthly' ? 'mês' : 'ano'}</span>
                    </div>
                 </div>
                 
                 <div className="p-8 flex-1 flex flex-col">
                    <div className="h-px bg-white/10 w-full mb-6"></div>
                    <ul className="space-y-4 mb-8 flex-1">
                       {STRIPE_PLANS.pro.features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-3 text-white font-bold">
                             <div className="mt-0.5 p-0.5 rounded-full bg-brand-50 text-slate-950">
                               <Check size={10} strokeWidth={4}/>
                             </div>
                             <span>{feat}</span>
                          </li>
                       ))}
                    </ul>
                    <button 
                       onClick={() => handlePlanSelection(STRIPE_PLANS.pro[cycle])}
                       disabled={loadingCheckout}
                       className="w-full py-4 rounded-xl bg-brand-600 text-white hover:bg-brand-500 font-black uppercase tracking-widest text-[10px] shadow-lg shadow-brand-600/20 transition-all flex items-center justify-center gap-2 group/btn"
                    >
                       {loadingCheckout ? <Loader2 size={16} className="animate-spin"/> : <span className="flex items-center gap-2">Assinar PRO+ Agora <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform"/></span>}
                    </button>
                 </div>
              </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-24 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
           <h3 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase mb-10 sm:mb-12">Dúvidas Comuns</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              {faqItems.map((item, i) => (
                <div key={i} className="bg-slate-50 dark:bg-slate-950 p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-100 dark:border-slate-800 hover:border-brand-200 transition-colors">
                   <p className="font-bold text-slate-900 dark:text-white mb-3 sm:mb-4 text-sm sm:text-base leading-tight">{item.q}</p>
                   <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium leading-relaxed">{item.a}</p>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="px-4 sm:px-6 py-20 sm:py-32 bg-brand-600 dark:bg-slate-950 rounded-t-[3rem] sm:rounded-t-[8rem] text-white text-center relative overflow-hidden">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent pointer-events-none"></div>
         <div className="relative z-10 max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-8xl font-black mb-6 tracking-tighter leading-none uppercase px-4 text-balance">COMECE A ECONOMIZAR <br/> <span className="text-brand-300">HOJE MESMO.</span></h2>
            <p className="text-brand-100 text-sm sm:text-2xl font-bold mb-10 sm:mb-16 max-w-2xl mx-auto opacity-80 px-6 leading-relaxed">O controle absoluto sobre sua obra está a apenas um clique de distância. Zero estresse, 100% organização.</p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button 
                onClick={() => onAuth()}
                className="group relative bg-white text-brand-600 px-10 sm:px-16 py-5 sm:py-7 rounded-2xl sm:rounded-full font-black text-xs sm:text-sm uppercase tracking-widest shadow-2xl hover:scale-105 active:scale-95 transition-all shadow-white/10 w-full sm:w-auto"
              >
                 Criar Projeto Gratuito
              </button>
            </div>
         </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t dark:border-slate-800 py-12 px-6">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
            <BrandLogo size="md" />
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
               {['Termos', 'Privacidade', 'Ajuda', 'Contato'].map(link => (
                 <button key={link} className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-brand-500 transition-colors">{link}</button>
               ))}
            </div>

            <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-slate-500">© 2024 Minha Obra S.A.</p>
         </div>
      </footer>

      {showPricingModal && (
        <PricingModal 
          onClose={() => setShowPricingModal(false)}
          onSelectPlan={handlePlanSelection}
          isLoading={loadingCheckout}
        />
      )}

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
};
